// Auth_Guard decision (pure) — extracted from the router's `beforeEach` so the
// decision logic can be property-tested in isolation (Property 9) without
// pulling the full route table and every page component into the test
// environment. The real guard in `router/index.js` delegates to this function,
// so testing it here exercises the actual behaviour.
//
// Given the navigation target `to` and the current session state, it returns
// either a redirect location object (the argument to pass to `next(...)`) or
// `null`, meaning "allow the navigation" (`next()`).
//
// Decisions (design §6, Property 9):
//   - Target requires auth AND session unauthenticated → redirect to the Login
//     page, capturing the target's full path (including query) as `redirect`
//     so it can be resumed after sign-in. Because the store's `isAuthenticated`
//     getter already accounts for token expiry, an expired token is treated as
//     unauthenticated here (Requirements 6.1, 6.3, 7.3).
//   - Target requires admin AND session authenticated but NOT an administrator
//     → redirect to the Admin_Shell landing. See the note below on why this is
//     not a redirect to Login.
//   - Target requires superadmin AND session authenticated but NOT a superadmin
//     (including an ordinary administrator) → same redirect.
//   - Target is the Login page AND session authenticated → redirect to the
//     Admin_Shell route (Requirement 6.5).
//   - Every other navigation is allowed, including all public routes and
//     authenticated access to admin routes (Requirements 6.4, 6.6, 12.1).
//
// The `session` argument accepts either a boolean (legacy: just
// `isAuthenticated`) or an object `{ authenticated, admin }`. Supporting the
// boolean form keeps every existing caller and test working unchanged; when a
// boolean is passed, `admin` is treated as false, so a `requiresAdmin` route is
// refused rather than allowed. That is the fail-closed default.
//
// NOTE: this is navigation only, never a security boundary. The role comes from
// a client-side store that a determined user can edit, so every admin-only
// endpoint is independently gated server-side by the `isAdmin` policy. The guard
// exists so non-admins do not see broken screens, not to protect data.
export function authGuardDecision(to, session) {
  const { authenticated, admin, superAdmin } = normalizeSession(session);

  const meta = (to && to.meta) || {};

  if (meta.requiresAuth && !authenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }

  // Superadmin-only route reached by an authenticated user who is not one —
  // including an ordinary administrator. Same treatment as the admin case
  // below: back to the shell with a reason, never to Login.
  if (meta.requiresSuperAdmin && authenticated && !superAdmin) {
    return { name: 'admin', query: { denied: to.name || 'admin' } };
  }

  // An authenticated non-administrator hitting an admin-only route is NOT sent
  // to Login: they are already signed in, and bouncing them to a login form
  // they would immediately be redirected back out of (see the rule below) would
  // loop. They are sent to the Admin_Shell landing, which every authenticated
  // user may view.
  //
  // An UNauthenticated visitor is handled by the rule above, which takes
  // precedence — so they get the login redirect (with resume path) they need,
  // not this one. Any `requiresAdmin` route should also carry `requiresAuth`;
  // the explicit `authenticated` check here keeps the decision correct even if
  // one is ever declared without it.
  if (meta.requiresAdmin && authenticated && !admin) {
    return { name: 'admin', query: { denied: to.name || 'admin' } };
  }

  if (to && to.name === 'login' && authenticated) {
    return { name: 'admin' };
  }

  return null;
}

// Accept either a boolean (`isAuthenticated`) or
// `{ authenticated, admin, superAdmin }`. Unknown shapes resolve to "logged out,
// no privileges" so a malformed argument denies access rather than granting it.
//
// `superAdmin` implies `admin`: a super administrator satisfies any
// administrator requirement, so callers need not pass both.
function normalizeSession(session) {
  if (typeof session === 'boolean') {
    return { authenticated: session, admin: false, superAdmin: false };
  }
  if (session && typeof session === 'object') {
    const superAdmin = session.superAdmin === true;
    return {
      authenticated: session.authenticated === true,
      admin: session.admin === true || superAdmin,
      superAdmin,
    };
  }
  return { authenticated: false, admin: false, superAdmin: false };
}

export default authGuardDecision;
